import { useState } from 'react'
import { History, Loader2, MessageSquareText, Search, Send, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { ToneBadge } from '@/components/composites/status-badge'
import { SEVERITY_LABEL, STATE_LABEL, useDeskAction, useDeskContext, type SupportCase } from './api'
import { when } from './Activity'

/**
 * The quick paths on a ticket (framework v4): the context pack someone picking it up reads first, and
 * the customer update drafted from engineering's notes, sent in one click. Neither changes the
 * ticket by itself; both only remove reading around and retyping.
 */

/** A summary of the conversation, tickets like it solved before, and the customer's other open tickets. */
export function ContextPanel({ c, onOpenCase }: { c: SupportCase; onOpenCase: (id: string) => void }) {
  const pack = useDeskContext(c.id)
  const data = pack.data
  return (
    <section className="grid gap-2 rounded-lg border border-border bg-card p-3">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold"><Sparkles className="size-4 text-brand" /> Context</h3>
      {pack.isPending ? (
        <p className="flex items-center gap-2 text-xs text-text-muted"><Loader2 className="size-3.5 animate-spin" /> Reading the conversation…</p>
      ) : !data ? (
        <p className="text-xs text-text-muted">The context could not be loaded.</p>
      ) : (
        <>
          {data.brief ? (
            <p className="text-sm leading-relaxed">{data.brief.text}</p>
          ) : (
            <p className="text-xs text-text-muted">{data.briefStatus === 'off' ? 'No AI summary in this environment.' : 'No summary yet: read the conversation below.'}</p>
          )}
          <div className="grid gap-1">
            <p className="flex items-center gap-1.5 text-xs font-medium text-text-secondary"><History className="size-3.5" /> Solved before</p>
            {data.similar.length ? (
              <ul className="grid gap-1">
                {data.similar.map((s) => (
                  <li key={s.id} className="rounded-md border border-border-soft px-2.5 py-1.5 text-xs">
                    <button type="button" className="font-medium text-brand hover:underline" onClick={() => onOpenCase(s.id)}>#{s.reference} {s.title}</button>
                    <span className="block text-text-secondary">
                      {[s.team, s.rootCause, s.fixedAt ? `fixed ${when(s.fixedAt)}` : null].filter(Boolean).join(' · ')}
                    </span>
                    {s.resolutionNote ? <span className="block">{s.resolutionNote}</span> : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-text-muted">Nothing similar with a recorded fix.</p>
            )}
          </div>
          {data.openForClient.length ? (
            <div className="grid gap-1">
              <p className="flex items-center gap-1.5 text-xs font-medium text-text-secondary"><Search className="size-3.5" /> {c.clientName ?? 'This customer'}’s other open tickets</p>
              <ul className="grid gap-1">
                {data.openForClient.map((o) => (
                  <li key={o.id} className="flex flex-wrap items-center gap-2 text-xs">
                    <button type="button" className="text-left text-brand hover:underline" onClick={() => onOpenCase(o.id)}>#{o.reference} {o.title}</button>
                    <ToneBadge tone="neutral" size="xs">{o.severity ? SEVERITY_LABEL[o.severity] : 'Untriaged'}</ToneBadge>
                    <span className="text-text-muted">{STATE_LABEL[o.state]}{o.team ? ` · ${o.team}` : ''}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      )}
    </section>
  )
}

/** The drafted customer update: edit if needed, then send through Intercom. It counts as the update. */
export function DraftPanel({ c, mayAct }: { c: SupportCase; mayAct: boolean }) {
  const run = useDeskAction()
  const [text, setText] = useState<string | null>(null)
  if (!c.draft || !mayAct) return null
  const value = text ?? c.draft.text
  return (
    <section className="grid gap-2 rounded-lg border border-border bg-brand-softer p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold"><MessageSquareText className="size-4 text-brand" /> Customer update, drafted</h3>
        <span className="text-tiny text-text-muted">From engineering’s notes · {when(c.draft.at)}</span>
      </div>
      <Textarea value={value} onChange={(e) => setText(e.target.value)} rows={4} className="bg-card text-sm" />
      <div className="flex items-center gap-2">
        <Button size="sm" disabled={run.isPending || !value.trim()} onClick={() => run.mutate({ id: c.id, action: 'send-draft', body: { text: value } }, { onSuccess: () => setText(null) })}>
          <Send className="size-4" /> Send to customer
        </Button>
        <span className="text-tiny text-text-muted">Goes out through Intercom and counts as the update.</span>
      </div>
    </section>
  )
}

/** "Reminders held until 4:30 PM by Vikas", or the blocker, while either is in force. */
export function holdNote(c: SupportCase): { label: string; detail: string; tone: 'danger' | 'neutral' } | null {
  if (!c.snoozedUntil) return null
  const hold = [...c.events].reverse().find((e) => (e.kind === 'snoozed' || e.kind === 'blocked') && e.until)
  if (!hold) return null
  return hold.kind === 'blocked'
    ? { label: 'Blocked', detail: `${hold.byName}: ${hold.text ?? ''} · reminders held until ${when(c.snoozedUntil)}`, tone: 'danger' }
    : { label: 'Reminders held', detail: `until ${when(c.snoozedUntil)} by ${hold.byName}`, tone: 'neutral' }
}
